/**
 * @solum/format — shared display formatters. One implementation of money / number
 * / date so surfaces don't re-derive them per page. The console had ~15 copies of
 * `eur` / `num` that had DRIFTED (0 vs 2 decimals, default vs en-US locale, "€" vs
 * "€ "), a genuine display-consistency bug; this is the single source.
 */

/** Coerce a possibly-null/blank/string value to a number (blank/null → 0). */
export function toNumber(v: unknown): number {
  return v == null || v === '' ? 0 : Number(v)
}

/**
 * Escape untrusted text for insertion into HTML text or quoted attributes.
 * This is not a sanitizer and is not valid for URL, JavaScript or CSS contexts.
 */
export function escapeHtmlText(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

export interface MoneyOptions {
  /** fixed fraction digits (default 0 for the symbol path; currency default when `currency` set) */
  decimals?: number
  /** currency symbol for the simple path (default '€'); ignored when `currency` is set */
  symbol?: string
  /** gap between symbol and number (default '' — some surfaces use ' ') */
  gap?: string
  /** BCP-47 locale for grouping/decimals (default: the runtime's) — a user's regional pref */
  locale?: string
  /** ISO 4217 code (EUR/USD/GBP…). When set, uses locale-correct currency formatting
   *  (symbol + placement per locale) instead of the fixed `symbol`+`gap` path. */
  currency?: string
}

/** Format a money value. With `currency`, locale-correct currency formatting
 *  ("$1,000.00", "1.000,00 €"); otherwise the simple `€1,000` symbol path. */
export function formatMoney(value: unknown, opts: MoneyOptions = {}): string {
  const { decimals, symbol = '€', gap = '', locale, currency } = opts
  const n = toNumber(value)
  if (currency) {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency,
      ...(decimals != null ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals } : {}),
    }).format(n)
  }
  const d = decimals ?? 0
  return symbol + gap + n.toLocaleString(locale, { minimumFractionDigits: d, maximumFractionDigits: d })
}

/**
 * Format a value using the currency carried by the record itself.
 *
 * Customer documents must keep the numeric amount visible even when legacy
 * data contains an unrecognised currency code, so this contract deliberately
 * falls back to a fixed amount plus the raw code instead of throwing.
 */
export function formatCurrencyAmount(value: unknown, currency: unknown, locale?: string): string {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '—'
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency: String(currency || 'EUR'),
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${String(currency ?? '')}`.trim()
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export interface DateOptions {
  withYear?: boolean
  /** BCP-47 locale — a user's regional pref (default: the built-in short "12 Jul"). */
  locale?: string
  /** IANA timezone (e.g. "Europe/Brussels") — a user's regional pref. */
  timeZone?: string
}

/** Short date: `12 Jul` (add `{ withYear: true }` → `12 Jul 2026`). Invalid/empty → `—`.
 *  With `locale`/`timeZone` (a user's regional prefs) it renders via Intl in their
 *  zone; otherwise the built-in short form (unchanged for existing call sites). */
/** The platform's default rendering convention: day-month, matching the
 *  non-Intl fallback path. Explicitly pinned so output never depends on the
 *  host's locale data. */
const DEFAULT_LOCALE = 'en-GB'

export function formatDate(iso: unknown, opts: DateOptions = {}): string {
  if (!iso) return '—'
  const d = new Date(String(iso))
  if (Number.isNaN(d.getTime())) return '—'
  if (opts.locale || opts.timeZone) {
    // `Intl.DateTimeFormat(undefined, …)` uses the HOST's locale. That made the
    // same call render '12 Jul' on a developer machine and 'Jul 12' on the CI
    // container — and it disagreed with the non-Intl branch below, which
    // hardcodes day-month. A formatter whose output depends on which machine
    // runs it is not a formatter; dates on an invoice must not change shape
    // because a server was rebuilt. Defaults to the same convention the
    // fallback path uses, so both branches always agree.
    return new Intl.DateTimeFormat(opts.locale ?? DEFAULT_LOCALE, {
      day: 'numeric', month: 'short', ...(opts.withYear ? { year: 'numeric' } : {}),
      ...(opts.timeZone ? { timeZone: opts.timeZone } : {}),
    }).format(d)
  }
  const base = `${d.getDate()} ${MONTHS[d.getMonth()]}`
  return opts.withYear ? `${base} ${d.getFullYear()}` : base
}

/* What a field KIND means to a value (#1679) — the headless half of the typed
   editor registry, so an import mapper and a bulk editor can parse a column
   without a React host. The renderer half lives in `@solum/ui`. */
export {
  FIELD_KINDS, TYPED_BLANK_KINDS, NUMERIC_KINDS,
  isFieldKind, isTypedBlank, isNumericKind,
  parseFieldValue, emptyFieldValue, emptyControlValue,
  formatPercent, formatFieldValue, formatMoneyField,
  type FieldKind,
} from './fieldKind.js'

/* Any error as a sentence a person may read (#3012) — the kit's copy of the
   SDK's `readableError`, so `@solum/ui` applies the rule without the SDK. */
export { readableError, isMachineText, DEFAULT_ERROR_SENTENCE } from './readableError.js'
